
import React, { useState, useRef, useCallback, memo } from "react";
import {
  Play,
  X,
  Loader2,
  Monitor,
  Smartphone,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import Club from "src/assets/club_example.png";
import Clubs from "src/assets/clubs_example.png";
import Players from "src/assets/players_example.png";

// Create a single optimized intersection observer
const useIntersectionObserver = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const elementRef = useRef(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setIsVisible(true);
          setHasAnimated(true);
          // Unobserve after animation to reduce overhead
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.1,
        rootMargin: "-50px 0px",
      }
    );

    const currentElement = elementRef.current;
    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [hasAnimated]);

  return [elementRef, isVisible];
};

// Memoized component to prevent unnecessary re-renders
const OptimizedScreenDisplay = memo(({ devicesInView }: { devicesInView: any }) => {
  return (
    <div className="relative">
      <div className="relative group">
        {/* Main Container - Simplified */}
        <div className="relative bg-white/95 rounded-[2rem] p-6 shadow-lg border border-gray-200/60 backdrop-blur-sm overflow-hidden">

          {/* Reduced background effects */}
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-blue-400/10 rounded-full blur-2xl" />
          <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-purple-400/10 rounded-full blur-2xl" />

          {/* Mock Screens Container */}
          <div className="relative space-y-6">
            {/* Desktop Screen */}
            <div className="relative">
              <div className="bg-slate-900 rounded-xl p-1 shadow-md">
                <div className="bg-slate-800 rounded-xl p-2">
                  <div className="relative bg-slate-900/30 rounded-lg overflow-hidden h-48">
                    <img
                      src={Clubs}
                      className="w-full h-full object-cover"
                      alt="clubs"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile/Tablet Screens Row */}
            <div className="flex gap-4">
              {/* Tablet Display */}
              <div className="flex-1">
                <div className="bg-slate-800 rounded-lg p-1">
                  <div className="bg-slate-700 rounded-lg p-2">
                    <div className="relative bg-slate-900/30 rounded overflow-hidden h-36">
                      <img
                        src={Club}
                        className="w-full h-full object-cover"
                        alt="club"
                        loading="lazy"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile Display */}
              <div className="flex-1">
                <div className="bg-slate-800 rounded-lg p-1">
                  <div className="bg-slate-700 rounded-lg p-2 pt-4">
                    <div className="relative bg-slate-900/30 rounded overflow-hidden h-36">
                      <img
                        src={Players}
                        className="w-full h-full object-cover"
                        alt="players"
                        loading="lazy"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

OptimizedScreenDisplay.displayName = 'OptimizedScreenDisplay';

// Optimized feature pills component
const FeaturePills = memo(({ featuresInView }: { featuresInView: any }) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap gap-2">
      {[
        {
          color: "blue",
          text: t("home.wizard_platform.features.ai_statistics"),
        },
        {
          color: "purple",
          text: t("home.wizard_platform.features.advanced_analysis"),
        },
        {
          color: "green",
          text: t("home.wizard_platform.features.multiplatform"),
        },
      ].map((feature, index) => (
        <div
          key={feature.color}
          className={`inline-flex items-center gap-2 bg-${feature.color}-100 text-${feature.color}-700 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-300 ${featuresInView
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-4"
            }`}
          style={{
            transitionDelay: `${index * 100}ms`,
          }}
        >
          <div className={`w-1.5 h-1.5 bg-${feature.color}-500 rounded-full`}></div>
          {feature.text}
        </div>
      ))}
    </div>
  );
});

FeaturePills.displayName = 'FeaturePills';

// Main component
export const WizardPlatform = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [modal, setModal] = useState(false);
  const [videoLoading, setVideoLoading] = useState(true);
  const { t } = useTranslation();

  // Use a single intersection observer for the entire section
  const [sectionRef, sectionInView] = useIntersectionObserver();

  // Throttled modal handlers
  const openModal = useCallback(() => {
    setModal(true);
    document.body.style.overflow = "hidden";
  }, []);

  const closeModal = useCallback(() => {
    setModal(false);
    setVideoLoading(true);
    document.body.style.overflow = "unset";
  }, []);

  const handleVideoLoad = useCallback(() => {
    setVideoLoading(false);
  }, []);

  return (
    <div
      //@ts-ignore
      ref={sectionRef}
      className="relative bg-white overflow-hidden"
    >
      {/* Simplified background */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-100/30 rounded-full blur-2xl"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-purple-100/20 rounded-full blur-2xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className={`inline-flex items-center justify-center mb-4 transition-all duration-300 ${sectionInView ? "opacity-100" : "opacity-0"
            }`}>
            <div className="flex items-center gap-2 bg-blue-50 rounded-full px-3 py-1.5 border border-blue-200">
              <Sparkles className="w-3 h-3 text-blue-500" />
              <span className="text-sm font-medium text-blue-700">
                {t("home.wizard_platform.advanced_platform")}
              </span>
            </div>
          </div>

          <h2 className={`text-4xl lg:text-6xl font-bold mb-4 transition-all duration-500 ${sectionInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}>
            <span className="bg-gradient-to-r from-slate-800 via-blue-900 to-purple-900 bg-clip-text text-transparent">
              {t("home.wizard_platform.title")}
            </span>
          </h2>

          <div className={`w-16 h-1 bg-gradient-to-r from-purple-500 to-blue-500 mx-auto rounded-full transition-all duration-500 ${sectionInView ? "opacity-100 scale-100" : "opacity-0 scale-0"
            }`}></div>
        </div>

        {/* Main Content */}
        <div className="lg:flex lg:items-center lg:gap-12">
          {/* Left Side - Devices Display */}
          <div className={`lg:w-1/2 mb-8 lg:mb-0 transition-all duration-500 ${sectionInView ? "opacity-100" : "opacity-0"
            }`}>
            <OptimizedScreenDisplay devicesInView={sectionInView} />
          </div>

          {/* Right Side - Content */}
          <div className={`lg:w-1/2 transition-all duration-500 ${sectionInView ? "opacity-100" : "opacity-0"
            }`}>
            <div className="space-y-6">
              {/* Description */}
              <div className="space-y-4">
                <p className="text-base lg:text-lg text-gray-700 leading-relaxed">
                  {t("home.wizard_platform.description")}
                </p>

                {/* Feature Pills */}
                <FeaturePills featuresInView={sectionInView} />
              </div>

              {/* CTA Button */}
              {/* <div className="pt-2">
                <button
                  onClick={openModal}
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                  className={`group relative inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold px-6 py-3 rounded-xl shadow transition-all duration-300 hover:shadow-md ${sectionInView ? "opacity-100" : "opacity-0"
                    }`}
                >
                  <div className="flex items-center gap-2">
                    <Play className="w-4 h-4 fill-current" />
                    <span>{t("home.wizard_platform.play_video")}</span>
                    <ArrowRight
                      className={`w-4 h-4 transition-all duration-300 ${isHovered ? "translate-x-1" : ""
                        }`}
                    />
                  </div>
                </button>
              </div> */}
            </div>
          </div>
        </div>
      </div>

      {/* Simplified Modal */}
      {modal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={closeModal}
        >
          <div
            className="relative w-full max-w-4xl mx-4 bg-white rounded-xl shadow-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 bg-gray-50 border-b">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                  <Play className="w-4 h-4 text-white fill-current" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">
                    {t("home.wizard_platform.modal.wizard_platform_demo")}
                  </h3>
                </div>
              </div>

              <button
                onClick={closeModal}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Video Container */}
            <div className="relative bg-black aspect-video">
              {videoLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                  <div className="text-center">
                    <Loader2 className="w-8 h-8 text-white animate-spin mx-auto mb-2" />
                    <p className="text-white/80 text-sm">
                      {t("home.wizard_platform.modal.loading_video")}
                    </p>
                  </div>
                </div>
              )}

              <iframe
                className="w-full h-full"
                src="https://public.wizardfootball.com/videos/WizardFootballPromo.mp4"
                title="Wizard Platform Demo"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                onLoad={handleVideoLoad}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};